import React, { useState, useEffect } from 'react';
import axios from 'axios';

const USE_CASES = [
  { id: 'report', name: 'Report Generation', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { id: 'analysis', name: 'Data Analysis', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  { id: 'summary', name: 'Summary', color: 'bg-green-100 text-green-700 border-green-200' },
  { id: 'insights', name: 'Insights', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { id: 'custom', name: 'Custom', color: 'bg-slate-100 text-slate-700 border-slate-200' },
];

const PromptTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [viewingTemplate, setViewingTemplate] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    system_prompt: '',
    user_prompt: '',
    use_case: '',
    agent_role: '',
    is_active: true
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/prompt-templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error loading templates:', error);
      showToast('Failed to load templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.system_prompt.trim()) {
      showToast('Name and system prompt are required', 'error');
      return;
    }

    try {
      if (editingTemplate) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/prompt-templates/${editingTemplate.id}`,
          formData
        );
        showToast('Template updated successfully');
      } else {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/prompt-templates`,
          formData
        );
        showToast('Template created successfully');
      }
      
      loadTemplates();
      resetForm();
    } catch (error) {
      console.error('Error saving template:', error);
      showToast(error.response?.data?.detail || 'Failed to save template', 'error');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      system_prompt: template.system_prompt,
      user_prompt: template.user_prompt || '',
      use_case: template.use_case || 'custom',
      agent_role: template.agent_role || '',
      is_active: template.is_active
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/prompt-templates/${id}`);
      showToast('Template deleted successfully');
      loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      showToast('Failed to delete template', 'error');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      system_prompt: '',
      user_prompt: '',
      use_case: 'custom',
      agent_role: '',
      is_active: true
    });
  };

  const getUseCaseInfo = (useCaseId) => {
    // Check if it matches a predefined use case
    const predefined = USE_CASES.find(uc => uc.id === useCaseId);
    if (predefined) {
      return predefined;
    }
    // Return custom format for any other text
    return {
      id: useCaseId,
      name: useCaseId || 'Custom',
      color: 'bg-slate-100 text-slate-700 border-slate-200'
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Toast */}
      {toast.show && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`${toast.type === 'success' ? 'bg-emerald-500' : 'bg-red-500'} text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2`}>
            <span>{toast.message}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Prompt Templates</h2>
          <p className="text-slate-600 text-sm mt-1">Manage system and user prompt templates</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-cyan-600 text-white rounded-lg font-medium hover:from-emerald-600 hover:to-cyan-700 transition-colors flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>New Template</span>
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider w-1/4">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider w-2/5">Description</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider w-1/6">Use Case</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-700 uppercase tracking-wider w-20">Prompts</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-700 uppercase tracking-wider w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {templates.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <svg className="w-12 h-12 text-slate-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-slate-600 font-medium">No templates yet</p>
                      <p className="text-slate-400 text-sm mt-1">Create your first prompt template to get started</p>
                    </div>
                  </td>
                </tr>
              ) : (
                templates.map(template => {
                  const useCaseInfo = getUseCaseInfo(template.use_case);
                  return (
                    <tr key={template.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`w-2 h-2 rounded-full mr-2 flex-shrink-0 ${template.is_active ? 'bg-emerald-500' : 'bg-slate-300'}`}></div>
                          <span className="text-sm font-medium text-slate-900 truncate">{template.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-slate-600 truncate">{template.description || '-'}</p>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded border ${useCaseInfo.color}`}>
                          {useCaseInfo.name}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-1">
                          <button
                            onClick={() => setViewingTemplate({ ...template, viewType: 'system' })}
                            className="p-1.5 text-cyan-600 hover:bg-cyan-50 rounded transition-colors"
                            title="View System Prompt"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                            </svg>
                          </button>
                          {template.user_prompt && (
                            <button
                              onClick={() => setViewingTemplate({ ...template, viewType: 'user' })}
                              className="p-1.5 text-purple-600 hover:bg-purple-50 rounded transition-colors"
                              title="View User Prompt"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-1">
                          <button
                            onClick={() => handleEdit(template)}
                            className="p-1.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors"
                            title="Edit"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(template.id)}
                            className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                            title="Delete"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Prompt Modal */}
      {viewingTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[80vh] flex flex-col overflow-hidden">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between flex-shrink-0 bg-white sticky top-0 z-10">
              <h3 className="text-lg font-semibold text-slate-900">
                {viewingTemplate.viewType === 'system' ? 'System Prompt' : 'User Prompt'}
              </h3>
              <button onClick={() => setViewingTemplate(null)} className="text-slate-400 hover:text-slate-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1 min-h-0">
              <div className="bg-slate-50 rounded-lg p-4">
                <pre className="text-sm text-slate-700 whitespace-pre-wrap font-mono">
                  {viewingTemplate.viewType === 'system' ? viewingTemplate.system_prompt : viewingTemplate.user_prompt}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-900">
                  {editingTemplate ? 'Edit Template' : 'New Template'}
                </h3>
                <button onClick={resetForm} className="text-slate-400 hover:text-slate-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Template name"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              
               <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Use Case<span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formData.use_case}
                  onChange={(e) => setFormData({ ...formData, use_case: e.target.value })}
                  placeholder="e.g., Report Generation, Data Analysis, Summary"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
                <p className="text-xs text-slate-500 mt-1">Enter the use case or category for this template</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Agent Role</label>
                <input
                  value={formData.agent_role}
                  onChange={(e) => setFormData({ ...formData, agent_role: e.target.value })}
                  placeholder="e.g., You are an expert real estate analyst specializing in commercial and residential portfolio management..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
                <p className="text-xs text-slate-500 mt-1">Define the AI agent's role and expertise for this template</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  System Prompt <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.system_prompt}
                  onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
                  placeholder="Enter system prompt"
                  rows={6}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-mono text-sm"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">User Prompt</label>
                <textarea
                  value={formData.user_prompt}
                  onChange={(e) => setFormData({ ...formData, user_prompt: e.target.value })}
                  placeholder="Enter user prompt (optional)"
                  rows={4}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-mono text-sm"
                />
              </div>
              
             
              
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
                />
                <label htmlFor="is_active" className="text-sm text-slate-700">
                  Active
                </label>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-cyan-600 text-white rounded-lg font-medium hover:from-emerald-600 hover:to-cyan-700"
                >
                  {editingTemplate ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptTemplates;
